import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "./Wishlist/Breadcrumb";
import Header from "./Header";
import CategoryNav from "./CategoryNav";
import Footer from "./Footersection";
import { FiPlus, FiMinus, FiTrash2 } from "react-icons/fi";
import { BASE_URL } from "../config/config";
import { useCart } from "../context/CartContext"; // <-- use your CartContext
import { OrdersContext } from "../context/OrdersContext";

const CartSection = () => {
  const navigate = useNavigate();

  // ---- CartContext hooks ----
  const {
    cart,
    updateQuantity,
    removeFromCart,
    clearCart,
    selectedAddress,
    selectAddress,
  } = useCart();

  // ---- Local UI state (non-cart) ----
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null); // stores applied promo
  const [promoMessage, setPromoMessage] = useState("");
  const [showCelebration, setShowCelebration] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [referralMessage, setReferralMessage] = useState("Apply");
  const [appliedReferral] = useState(null);
  const [referralApplied, setReferralApplied] = useState(false);

  const [error, setError] = useState("");
  const [orderID, setOrderID] = useState("");
  const [showAddressList, setShowAddressList] = useState(false);

  const [showCheckoutSuccess, setShowCheckoutSuccess] = useState(false);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const { refreshOrders } = useContext(OrdersContext);

  const [newAddress, setNewAddress] = useState({
    name: "",
    address: "",
    phone: "",
    pincode: "",
    city: "",
    state: "",
  });

  const [savedAddresses, setSavedAddresses] = useState([]);

  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("user_id");

  // ---- Fetch user addresses ----
  useEffect(() => {
    if (!token || !userId) return;

    const fetchAddresses = async () => {
      try {
        const response = await fetch(`${BASE_URL}/useraddresses/${userId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });

        const data = await response.json();
        if (data.status === 200) {
          const activeAddresses = data.data || [];
          setSavedAddresses(activeAddresses);

          // If none selected yet, default to first
          if (!selectedAddress && activeAddresses.length > 0) {
            selectAddress(activeAddresses[0]);
          }
        } else {
          console.error("Failed to fetch addresses", data);
        }
      } catch (error) {
        console.error("Error fetching addresses:", error);
      }
    };

    fetchAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userId]);

  // ---- Promo ----
  const handleApplyPromo = async () => {
    if (!promoCode.trim()) {
      setPromoMessage("Please enter a promo code.");
      return;
    }

    if (!token || !userId) {
      alert("User not authenticated");
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/promocode/apply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: userId,
          promocode: promoCode,
          order_amount: subtotal, // subtotal is numeric
        }),
      });

      const data = await response.json();

      if (data.status === "success") {
        const promo = data.data;

        // Save promo details in numeric format
        setAppliedPromo({
          promocode: promo.promocode,
          discount: parseFloat(promo.discount),
          type: promo.discount_type, // 'percentage' or 'flat'
        });

        setPromoMessage(`Promo code "${promo.promocode}" applied!`);
        setShowCelebration(true);
        setTimeout(() => setShowCelebration(false), 3000);

        setPromoCode(""); // clear input
      } else {
        setAppliedPromo(null);
        setPromoMessage(data.message || "Invalid promo code");
      }
    } catch (error) {
      console.error("Error applying promo code:", error);
      setPromoMessage("Failed to apply promo code. Try again.");
      setAppliedPromo(null);
    }
  };

  const handleReferralApply = () => {
    if (referralCode.trim() === "") {
      setError("Please Fill Referral Code");
      setReferralApplied(false);
      setReferralMessage("");
      return;
    }

    setError("");
    setReferralApplied(true);
    setReferralMessage(`Referral code "${referralCode}" applied successfully!`);
    alert(`Referral code "${referralCode}" applied successfully!`);
  };

  const handlePlaceOrder = () => {
    if (!referralApplied) {
      setError("Referral code is mandatory before placing the order.");
      return;
    }
    setError("");
    alert("Order placed successfully!");
  };

  // ---- Address modal helpers ----
  const handleAddAddress = () => setShowAddressList(true);
  const handleSelectAddress = (addr) => {
    selectAddress(addr);
    setShowAddressList(false);
  };

  const handleNewAddressSubmit = async (e) => {
    e.preventDefault();

    // Trim values
    const name = newAddress.name.trim();
    const phone = newAddress.phone.trim();
    const address = newAddress.address.trim();
    const pincode = newAddress.pincode.trim();
    const city = newAddress.city.trim();
    const state = newAddress.state.trim();

    // Validation
    if (!name || !phone || !address || !pincode || !city || !state) {
      alert("Please fill all fields");
      return;
    }

    if (!/^[A-Za-z\s]{3,}$/.test(name)) {
      alert("Name must be at least 3 characters and contain only letters and spaces");
      return;
    }

    if (!/^\d{10}$/.test(phone)) {
      alert("Phone number must be 10 digits");
      return;
    }

    if (!/^\d{6}$/.test(pincode)) {
      alert("Pincode must be 6 digits");
      return;
    }

    if (!token || !userId) {
      alert("User not authenticated");
      return;
    }

    const payload = {
      user_id: userId,
      name,
      phone,
      address,
      city,
      country: state,
      pin: pincode,
      status: "1",
      create_audit_id: userId,
    };

    try {
      const response = await fetch(`${BASE_URL}/user-addresses`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.status === 201) {
        const newAddr = {
          id: data.data.id,
          name: payload.name,
          address: `${payload.address}, ${payload.city}, ${payload.country} - ${payload.pin}`,
          phone: payload.phone,
        };

        setSavedAddresses((prev) => [...prev, newAddr]);
        selectAddress(newAddr);

        setNewAddress({
          name: "",
          address: "",
          phone: "",
          pincode: "",
          city: "",
          state: "",
        });

        setShowNewAddressForm(false);
        setShowAddressList(false);
      } else {
        alert(`Failed to add address: ${data.message || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Error adding address:", error);
      alert("Error adding address. Check console for details.");
    }
  };

  // ---- Cart handlers (context) ----
  const increaseQty = (id) => {
    const current = cart.find((i) => i.id === id);
    if (!current) return;
    updateQuantity(id, (current.quantity || 1) + 1);
  };

  const decreaseQty = (id) => {
    const current = cart.find((i) => i.id === id);
    if (!current) return;
    if ((current.quantity || 1) <= 1) {
      removeFromCart(id);
    } else {
      updateQuantity(id, (current.quantity || 1) - 1);
    }
  };

  const handleRemoveItem = (id) => {
    removeFromCart(id);
  };

  const handleClearCart = () => {
    clearCart();
  };

  // ---- Totals ----
  const subtotal = cart.reduce(
    (sum, item) => sum + (item.offer_price * item.quantity),
    0
  );

  const shipping = subtotal > 50000 ? 0 : 0;
  const tax = Math.round(subtotal * 1);

  const promoDiscount = appliedPromo
    ? appliedPromo.type.toLowerCase() === "percentage"
      ? Math.round(subtotal * (appliedPromo.discount / 100))
      : appliedPromo.discount
    : 0;

  const referralDiscount = appliedReferral
    ? appliedReferral.type === "Percentage Type"
      ? Math.round(subtotal * (appliedReferral.discount / 100))
      : appliedReferral.discount
    : 0;

  const discount = promoDiscount + referralDiscount;

  const grandTotal = subtotal + shipping - discount;
  const totalQuantity = cart.reduce((sum, item) => sum + item.quantity, 0);

  // ---- Checkout ----
  const handleProceed = async () => {
    if (!referralApplied) {
      alert("Referral code is mandatory before placing the order.");
      return;
    }
    if (cart.length === 0) {
      alert("Your cart is empty.");
      return;
    }
    if (!selectedAddress) {
      alert("Please select a delivery address.");
      return;
    }
    if (!token || !userId) {
      alert("User not authenticated");
      return;
    }

    const subtotalCalc = cart.reduce(
      (sum, item) => sum + (item.offer_price * item.quantity),
      0
    );

    // Since UI shows shipping free, set shippingCalc to 0
    const shippingCalc = 0;

    const taxCalc = Math.round(subtotalCalc * 0.18);

    const discountCalc = appliedPromo
      ? (
          appliedPromo.type.toLowerCase() === "percentage"
          ? Math.round(subtotalCalc * (parseFloat(appliedPromo.discount) / 100))
          : parseFloat(appliedPromo.discount)
        )
      : 0;

    const totalAmount = subtotalCalc + shippingCalc - discountCalc;


    const productsPayload = cart.map((item) => ({
      product_id: item.id,
      quantity: item.quantity,
      price: item.offer_price,
      offer_price: item.offerPrice || item.offer_price,
    }));

    const payload = {
      user_id: userId,
      address_id: selectedAddress.id,
      create_audit_id: userId,
      tax: 0,
      total_amount: totalAmount,
      discount: discountCalc,
      products: productsPayload,
      referralCode: referralCode,
    };

    try {
      const response = await fetch(`${BASE_URL}/place-order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && data.status === 201) {
        console.log("Order Created:", data);

        const internalOrderId = data.order_id;

        // NOW: Create Razorpay Order
        const razorResponse = await fetch(`${BASE_URL}/payment/create-razorpay-order`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            order_id: internalOrderId, // YOUR ORDER ID
            amount: totalAmount        // amount in rupees
          }),
        });

        const razorData = await razorResponse.json();

        // OPEN RAZORPAY FORM
        openRazorpayPayment(
          razorData.order_id,  // Razorpay order id
          totalAmount,         // amount
          internalOrderId      // your backend order id
        );

      } else {
        alert(data.message || "Failed to place order. Please try again.");
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Something went wrong. Try again.");
    }
  };

  const openRazorpayPayment = async (rzpOrderId, amount, receiptOrderId) => {
    try {
      // 🔐 1. Send token to backend
      const keyResponse = await fetch(`${BASE_URL}/payment/getKey`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`, // or cookies
        },
      });

      const { key } = await keyResponse.json();

      const options = {
        key, 
        amount: amount * 100,
        currency: "INR",
        name: "One Stop Wedding Dresses",
        description: "Order Payment",
        order_id: rzpOrderId,

        handler: function (response) {
          verifyPayment(response, receiptOrderId);
        },

        theme: { color: "#3399cc" },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error("Error loading Razorpay:", error);
    }
  };



  const verifyPayment = async (response, receiptOrderId) => {
    try {
      const res = await fetch(`${BASE_URL}/payment/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_order_id: response.razorpay_order_id,
          razorpay_signature: response.razorpay_signature,
          receipt_id: receiptOrderId, // your original order_id
        }),
      });

      const result = await res.json();

      if (result.status === "success") {
        // alert("Payment Successful! 🎉");
        // navigate("/order-success");
        setShowCheckoutSuccess(true);
        // setOrderID(data.order_id);       
        refreshOrders?.(); // refresh orders in context
        clearCart(); // context clears and persists
      } else {
        alert("Payment verification failed");
      }
    } catch (error) {
      console.error("Verify error:", error);
    }
  };


  // ---- Breadcrumb ----
  const breadcrumbItems = [
    { label: "Home", path: "/", current: false },
    { label: "Cart", path: "/cart", current: true },
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden box-border" style={{"-webkit-font-smoothing":"antialiased"}}>
      <Header />
      {/* <CategoryNav /> */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        <div className="mb-8">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        {/* Use flex with min-w-0 on children so items can shrink and not cause overflow */}
        <div className="w-full flex flex-col lg:flex-row gap-10 justify-between">

          {/* LEFT: Cart items - make flexible and allow shrink */}
          <div className="flex-1 min-w-0">
            <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 overflow-hidden">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4">
                <h2 className="text-xl sm:text-2xl font-semibold text-neutral-900">
                  Shopping Cart
                </h2>

                {cart.length > 0 && (
                  <button
                    onClick={handleClearCart}
                    className="text-red-600 hover:text-red-700 font-medium text-sm self-start sm:self-auto"
                  >
                    Clear Cart
                  </button>
                )}
              </div>


              {cart.length === 0 ? (
                <div className="text-center py-12">
                  <div className="mb-4">
                    <svg
                      className="mx-auto h-16 w-16 text-gray-300"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-1.5 1.5M7 13l1.5 1.5M17 21a2 2 0 100-4 2 2 0 000 4zM9 21a2 2 0 100-4 2 2 0 000 4z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-neutral-700 mb-2">
                    Your cart is empty
                  </h3>
                  <p className="text-neutral-500 mb-6">
                    Add some beautiful sarees to get started!
                  </p>
                  <button
                    onClick={() => navigate("/products")}
                    className="inline-flex items-center px-6 py-3 bg-yellow-500 text-white font-medium rounded-lg hover:bg-yellow-600 transition-colors duration-200"
                  >
                    Continue Shopping
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {cart.map((item) => (
                    <div
                      key={item.id}
                      className="border rounded-lg p-4 hover:bg-gray-50 transition-colors overflow-hidden"
                    >
                      {/* Desktop row */}
                      <div className="hidden sm:flex items-center gap-4 min-w-0">

                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-20 h-20 object-cover rounded-md flex-shrink-0"
                        />

                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-neutral-900 mb-1 truncate">
                            {item.name}
                          </h3>
                          <p className="text-sm text-neutral-600 truncate">
                            {item.category} • {item.fabric}
                          </p>
                          <p className="text-lg font-semibold text-neutral-900 mt-2 flex items-center gap-2">

                            {/* Offer Price */}
                            <span className="text-lg text-yellow-600">
                              ₹ {item.offer_price}/-
                            </span>
                            
                            {/* Original Price (crossed out if there’s an offer) */}
                            {item.price !== item.offer_price && (
                              <span className="text-sm text-neutral-500 line-through">
                                ₹ {item.price}/-
                              </span>
                            )}

                            {/* Discount % */}
                            {item.price !== item.offer_price && Number(item.price) > Number(item.offer_price) && (
                              <span className="text-xs text-green-600">
                                ({Math.round(((Number(item.price) - Number(item.offer_price)) / Number(item.price)) * 100)}% off)
                              </span>
                            )}
                          </p>

                        </div>

                        <div className="flex items-center gap-3 flex-shrink-0">
                          <button
                            onClick={() => decreaseQty(item.id)}
                            className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 hover:bg-gray-100 transition-colors"
                            aria-label="Decrease quantity"
                          >
                            <FiMinus size={14} />
                          </button>

                          <span className="w-8 text-center font-medium">
                            {item.quantity}
                          </span>

                          <button
                            onClick={() => increaseQty(item.id)}
                            className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 hover:bg-gray-100 transition-colors"
                            aria-label="Increase quantity"
                          >
                            <FiPlus size={14} />
                          </button>
                        </div>

                        <button
                          onClick={() => handleRemoveItem(item.id)}
                          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors flex-shrink-0"
                          aria-label="Remove item"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </div>

                      {/* Mobile row */}
                      <div className="sm:hidden">
                        <div className="flex gap-3 mb-3">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-16 h-16 object-cover rounded-md flex-shrink-0"

                          />
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-neutral-900 mb-1 text-sm leading-tight truncate">
                              {item.name}
                            </h3>
                            <p className="text-xs text-neutral-600 mb-1 truncate">
                              {item.category} • {item.fabric}
                            </p>
                            <p className="text-lg font-semibold text-neutral-900 mt-2 flex items-center gap-2">
  {/* Original Price (crossed out if there’s an offer) */}
  {item.price !== item.offer_price && (
    <span className="text-sm text-neutral-500 line-through">
      ₹ {item.price}/-
    </span>
  )}

  {/* Offer Price */}
  <span className="text-lg text-yellow-600">
    ₹ {item.offer_price}/-
  </span>

  {/* Discount % */}
  {item.price !== item.offer_price && Number(item.price) > Number(item.offer_price) && (
    <span className="text-xs text-green-600">
      ({Math.round(((Number(item.price) - Number(item.offer_price)) / Number(item.price)) * 100)}% off)
    </span>
  )}
</p>

                          </div>
                          <button
                            onClick={() => handleRemoveItem(item.id)}
                            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors self-start"
                            aria-label="Remove item"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>

                        <div className="flex items-center justify-center gap-4 bg-gray-50 rounded-lg p-3">
                          <button
                            onClick={() => decreaseQty(item.id)}
                            className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-300 bg-white hover:bg-gray-100 transition-colors"
                            aria-label="Decrease quantity"
                          >
                            <FiMinus size={16} />
                          </button>

                          <div className="flex items-center gap-2">
                            <span className="text-sm text-neutral-600">Qty:</span>
                            <span className="text-lg font-medium text-neutral-900 min-w-[2rem] text-center">
                              {item.quantity}
                            </span>
                          </div>

                          <button
                            onClick={() => increaseQty(item.id)}
                            className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-300 bg-white hover:bg-gray-100 transition-colors"
                            aria-label="Increase quantity"
                          >
                            <FiPlus size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: Summary / Address - keep a safe max width and allow shrinking on zoom */}
          <div className="w-full max-w-full lg:w-96 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 sticky top-8">
              <div className="mb-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 gap-2">
                  <h3 className="text-lg font-semibold text-neutral-900">
                    Delivery Address
                  </h3>
                  <button
                    onClick={handleAddAddress}
                    className="text-yellow-600 font-medium hover:text-yellow-700 text-sm self-start sm:self-auto"
                  >
                    {selectedAddress ? "Change" : "+ Add"} Address
                  </button>
                </div>

                {selectedAddress ? (
                  <div className="border rounded-lg p-3 bg-gray-50">
                    <p className="font-medium text-neutral-900">
                      {selectedAddress.name}
                    </p>
                    <p className="text-sm text-neutral-600 mt-1 break-words">
                      {selectedAddress.address}
                    </p>
                    <p className="text-sm text-neutral-600">
                      {selectedAddress.phone}
                    </p>
                  </div>
                ) : (
                  <p className="text-neutral-500 text-sm">No address selected</p>
                )}
              </div>

              <div className="mb-6">
                <h3 className="text-sm font-semibold text-neutral-900 mb-3">
                  Promo Code
                </h3>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    placeholder="Enter promo code"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent w-full max-w-full"
                  />
                  <button
                    onClick={handleApplyPromo}
                    className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 text-sm font-medium transition-colors whitespace-nowrap"
                  >
                    Apply
                  </button>
                </div>
                {promoMessage && (
                  <p
                    className={`text-sm mt-2 ${
                      promoMessage.includes("applied") ||
                      promoMessage.includes("saved")
                        ? "text-green-600"
                        : "text-red-500"
                    }`}
                  >
                    {promoMessage}
                  </p>
                )}
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Referral Code <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-col gap-1">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={referralCode}
                      onChange={(e) => {
                        setReferralCode(e.target.value);
                        if (error) setError(""); // remove error as user types
                      }}
                      className={`flex-grow border rounded-md px-3 py-2 text-sm ${
                        error ? "border-red-500" : "border-gray-300"
                      } w-full max-w-full`}
                      placeholder="Enter referral code"
                    />
                    <button
                      onClick={handleReferralApply}
                      className={`px-4 py-2 rounded-md text-white text-sm ${
                        referralApplied
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-green-600 hover:bg-green-700"
                      }`}
                      disabled={referralApplied} // prevent re-click
                    >
                      {referralApplied ? "Applied" : "Apply"}
                    </button>
                  </div>
                  {error && <p className="text-red-500 text-xs">{error}</p>}
                </div>


              </div>


              {cart.length > 0 && (
                <>
                  <div className="border-t pt-4">
                    <h3 className="text-lg font-semibold text-neutral-900 mb-4">
                      Order Summary
                    </h3>

                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-neutral-600">Subtotal</span>
                        <span className="font-medium">
                          ₹ {subtotal.toLocaleString()}
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-neutral-600">Shipping</span>
                        <span className="font-medium">
                          {shipping === 0 ? (
                            <span className="text-green-600">Free</span>
                          ) : (
                            `₹ ${shipping.toLocaleString()}`
                          )}
                        </span>
                      </div>

                      {/* <div className="flex justify-between">
                        <span className="text-neutral-600">Tax (GST 18%)</span>
                        <span className="font-medium">
                          ₹ {tax.toLocaleString()}
                        </span>
                      </div> */}

                      {discount > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span>Discount</span>
                          <span className="font-medium">
                            -₹ {discount.toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="border-t mt-4 pt-4">
                      <div className="flex justify-between text-lg font-semibold">
                        <span>Total</span>
                        <span>₹ {grandTotal.toLocaleString()}</span>
                      </div>
                    </div>

                    {shipping > 0 && (
                      <p className="text-xs text-neutral-500 mt-2">
                        Add ₹ {(50000 - subtotal).toLocaleString()} more for free
                        shipping
                      </p>
                    )}
                  </div>

                  <button
                    onClick={handleProceed}
                    className={`w-full mt-6 py-3 rounded-lg font-medium transition-colors ${
                      cart.length === 0 || !selectedAddress || !referralApplied
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-yellow-500 text-white hover:bg-yellow-600"
                    }`}
                    disabled={cart.length === 0 || !selectedAddress || !referralApplied}
                  >
                    Proceed to Checkout
                  </button>

                </>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Address selection modal */}
      {showAddressList && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-neutral-900">
                  Select Delivery Address
                </h3>
                <button
                  onClick={() => setShowAddressList(false)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  aria-label="Close modal"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="space-y-3 max-h-60 overflow-y-auto">
                <div
                  onClick={() => {
                    setShowAddressList(false);   // hide the list
                    setShowNewAddressForm(true); // show the form
                  }}
                  className="border-2 border-dashed border-yellow-400 rounded-lg p-4 cursor-pointer hover:bg-yellow-50 transition-colors text-center"
                >
                  <div className="flex items-center justify-center mb-2">
                    <svg
                      className="w-6 h-6 text-yellow-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                  </div>
                  <p className="font-medium text-yellow-600">Add New Address</p>
                  <p className="text-xs text-neutral-500 mt-1">
                    Click to add a new delivery address
                  </p>
                </div>

                {savedAddresses.map((addr) => (
                  <div
                    key={addr.id}
                    onClick={() => handleSelectAddress(addr)}
                    className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                      selectedAddress?.id === addr.id
                        ? "border-yellow-500 bg-yellow-50"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    <p className="font-medium text-neutral-900">{addr.name}</p>
                    <p className="text-sm text-neutral-600 mt-1 break-words">
                      {addr.address}
                    </p>
                    <p className="text-sm text-neutral-600">{addr.phone}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <button
                  onClick={() => setShowAddressList(false)}
                  className="flex-1 py-2 border border-gray-300 text-neutral-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (!selectedAddress) {
                      alert("Please select an address first.");
                      return;
                    }
                    setShowAddressList(false);
                  }}
                  className="flex-1 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
                >
                  Confirm Address
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Address Form Modal */}
      {showNewAddressForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-neutral-900">Add New Address</h3>
                <button
                  onClick={() => setShowNewAddressForm(false)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  aria-label="Close modal"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleNewAddressSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Full Name *</label>
                  <input
                      type="text"
                      value={newAddress.name}
                      onChange={(e) => {
                        let value = e.target.value;

                        // Allow only letters and spaces
                        if (/^[A-Za-z ]*$/.test(value)) {
                          // Enforce max 30 characters
                          if (value.length <= 50) {
                            setNewAddress({ ...newAddress, name: value });
                          }
                        }
                      }}
                      maxLength={50}   // optional: also blocks typing after 50 chars
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      placeholder="Enter your full name"
                      required
                    />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Address *</label>
                  <textarea
                    value={newAddress.address}
                    onChange={(e) => setNewAddress({...newAddress, address: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    placeholder="House no, Building, Street, Area"
                    rows="3"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">City *</label>
                    <input
  type="text"
  value={newAddress.city}
  onChange={(e) => {
    const value = e.target.value;

    // Allow only letters and spaces
    if (/^[A-Za-z ]*$/.test(value)) {
      // Limit to 30 characters
      if (value.length <= 30) {
        setNewAddress({ ...newAddress, city: value });
      }
    }
  }}
  maxLength={30}
  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
  placeholder="City"
  required
/>

                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">State *</label>
                    <input
  type="text"
  value={newAddress.state}
  onChange={(e) => {
    const value = e.target.value;

    // Allow only letters and spaces
    if (/^[A-Za-z ]*$/.test(value)) {
      // Limit to 20 characters
      if (value.length <= 20) {
        setNewAddress({ ...newAddress, state: value });
      }
    }
  }}
  maxLength={20}
  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
  placeholder="State"
  required
/>

                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Pincode *</label>
                    <input
  type="text"
  value={newAddress.pincode}
  onChange={(e) => {
    const value = e.target.value;

    // Allow only numbers
    if (/^[0-9]*$/.test(value)) {
      // Limit to 6 digits
      if (value.length <= 6) {
        setNewAddress({ ...newAddress, pincode: value });
      }
    }
  }}
  maxLength={6}
  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
  placeholder="Pincode"
  required
/>

                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Phone *</label>
                    <input
  type="tel"
  value={newAddress.phone}
  onChange={(e) => {
    const value = e.target.value;

    // Allow only numbers
    if (/^[0-9]*$/.test(value)) {
      // Limit to 11 digits
      if (value.length <= 10) {
        setNewAddress({ ...newAddress, phone: value });
      }
    }
  }}
  maxLength={10}
  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
  placeholder="Phone number"
  required
/>

                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowNewAddressForm(false)}
                    className="flex-1 py-2 border border-gray-300 text-neutral-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
                  >
                    Save Address
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}


      {/* Checkout success */}
      {showCheckoutSuccess && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 text-center">
              <div className="mb-4">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-green-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-2">
                Order Placed Successfully! 🎉<br/>
                <span className="text-sm font-normal text-neutral-600">Order ID: {orderID}</span>
              </h3>
              <p className="text-neutral-600 mb-4">
                Thank you for your purchase. Your order has been confirmed and
                will be delivered to your selected address.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => {
                    setShowCheckoutSuccess(false);
                    navigate("/");
                  }}
                  className="flex-1 py-2 border border-gray-300 text-neutral-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Continue Shopping
                </button>
                <button
                  onClick={() => {
                    setShowCheckoutSuccess(false);
                    navigate("/profile/orders");
                  }}
                  className="flex-1 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
                >
                  Track Order
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CartSection;